import { Classes, Dialog } from "@blueprintjs/core"
import { LocalLoader } from "modules/common/components/loader/Loader"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import {
  DefaultSelectedOption,
  filterItemPredicate,
  renderSingleOption,
  SimpleSelect,
} from "modules/common/services/form/select"
import React, { useCallback, useEffect, useState } from "react"
import { BoardColumnSnapshotType, PortfolioSnapshotType } from "../models/board"
import { QuestionCardType } from "../models/card"
import styles from "./MoveToBoardDialog.module.scss"

/** Общая доска по проектам: у неё нет идентификатора, поэтому в списке она отдельным пунктом */
const PROJECTS_BOARD = "orders"

/**
 * Перенос задания на другую доску. Колонки грузятся только после выбора доски:
 * досок у сотрудника может быть много, а нужна колонка ровно одной из них
 */
export const MoveToBoardDialog: React.FC<MoveToBoardDialogProps> = ({
  card,
  portfolios,
  currentBoardId,
  canUseProjectsBoard,
  onLoadColumns,
  onSubmit,
  onCancel,
}) => {
  const [boardId, setBoardId] = useState("")
  const [columnId, setColumnId] = useState("")
  const [columns, setColumns] = useState<BoardColumnSnapshotType[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const boards: SelectItem[] = [
    ...(canUseProjectsBoard && currentBoardId ? [{ id: PROJECTS_BOARD, label: "Задания по проектам" }] : []),
    ...portfolios.filter((p) => p.id !== currentBoardId).map((p) => ({ id: p.id, label: p.name })),
  ]

  const board = boards.find((b) => b.id === boardId) || null
  const column = columns.map((c) => ({ id: c.key, label: c.name })).find((c) => c.id === columnId) || null

  const selectBoard = useCallback(
    async (id: string) => {
      setBoardId(id)
      setColumnId("")
      setColumns([])

      if (!id) {
        return
      }

      setLoading(true)
      const loaded = await onLoadColumns(id === PROJECTS_BOARD ? "" : id)
      setLoading(false)

      setColumns(loaded)

      // колонку почти всегда берут первую — подставляем её сразу
      loaded.length > 0 && setColumnId(loaded[0].key)
    },
    [onLoadColumns]
  )

  // доска в списке одна — выбирать не из чего, открываем её колонки сразу
  useEffect(() => {
    boards.length === 1 && !boardId && selectBoard(boards[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submit = useCallback(async () => {
    if (!boardId || !columnId) {
      return
    }

    setSaving(true)
    await onSubmit(boardId === PROJECTS_BOARD ? "" : boardId, columnId)
    setSaving(false)
  }, [boardId, columnId, onSubmit])

  return (
    <Dialog
      isCloseButtonShown={true}
      isOpen={true}
      onClose={onCancel}
      title="Перенести на доску"
      backdropClassName="standard"
      className={`figma-dialog ${styles.dialog}`}
    >
      <div className={styles.body}>
        <div className={styles.question} title={card.title}>
          <span className={styles.number}>{card.number}</span>
          {card.label}
        </div>

        <div className={styles.field}>
          <label className="bp6-label">Доска</label>
          <SimpleSelect
            className={`${Classes.FILL} planr-default-input`}
            filterable={boards.length > 6}
            itemPredicate={filterItemPredicate}
            activeItem={board}
            items={boards}
            itemRenderer={renderSingleOption}
            onItemSelect={(item: SelectItem) => selectBoard(item ? item.id : "")}
            inputProps={{ placeholder: "Куда перенести" }}
            popoverProps={{ rootBoundary: "viewport", popoverClassName: "import-spendings-table__select" }}
          >
            <DefaultSelectedOption option={board} what="Выберите доску" />
          </SimpleSelect>
        </div>

        <div className={styles.field}>
          <label className="bp6-label">Колонка</label>

          {loading ? (
            <div className={styles.loading}>
              <LocalLoader active={true} />
            </div>
          ) : (
            <SimpleSelect
              className={`${Classes.FILL} planr-default-input`}
              filterable={false}
              activeItem={column}
              items={columns.map((c) => ({ id: c.key, label: c.name }))}
              itemRenderer={renderSingleOption}
              onItemSelect={(item: SelectItem) => setColumnId(item ? item.id : "")}
              disabled={!boardId || columns.length === 0}
              popoverProps={{ rootBoundary: "viewport", popoverClassName: "import-spendings-table__select" }}
            >
              <DefaultSelectedOption
                option={column}
                disabled={!boardId || columns.length === 0}
                what={boardId ? "Выберите колонку" : "Сначала выберите доску"}
              />
            </SimpleSelect>
          )}
        </div>

        {card.childrenCount > 0 && (
          <div className={styles.notice}>
            У задания есть подзадания — перед переносом спросим, забирать ли их с собой.
          </div>
        )}
      </div>

      <div className={Classes.DIALOG_FOOTER}>
        <div className={styles.footer}>
          <PlanrButton type="greenish" onClick={submit} disabled={!boardId || !columnId || saving}>
            Перенести
          </PlanrButton>
          <PlanrButton type="graybtn" onClick={onCancel}>
            Отмена
          </PlanrButton>
        </div>
      </div>
    </Dialog>
  )
}

interface MoveToBoardDialogProps {
  card: QuestionCardType
  /** Доски сотрудника: его собственные и те, куда его пригласили */
  portfolios: PortfolioSnapshotType[]
  /** Открытая доска: на неё же переносить нечего */
  currentBoardId: string
  /** Может ли сотрудник заводить задания на доске по проектам */
  canUseProjectsBoard: boolean
  onLoadColumns: (boardId: string) => Promise<BoardColumnSnapshotType[]>
  onSubmit: (boardId: string, columnId: string) => Promise<void>
  onCancel: () => void
}
